import { useEffect } from 'preact/hooks';
import type { ComponentChildren } from 'preact';
import type { WidgetState } from '../state';
import { track } from '../lib/analytics';

export type Step3Props = {
  state: WidgetState;
  update: (patch: Partial<WidgetState>) => void;
  /**
   * The aerial, passed in rather than constructed here.
   *
   * Injected for the same reason steps 1 and 2 inject their callables: `RoofAerial`
   * needs a Maps key and a live script, and a component test should not need either.
   * It carries its own test for the accessible name and the no-key fallback.
   */
  aerial: ComponentChildren;
};

/** Whole kWh with a thousands separator. Formatting only; the value is not recomputed. */
const kwh = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
/** One decimal, which is the precision the array size is meaningful to. */
const kw = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

/**
 * Step 3. What the roof will fit, or the message for a roof Google could not read.
 *
 * NO DOLLAR FIGURE, NO CURRENCY SYMBOL, NO SAVINGS CLAIM. That is ADR-0016 and it is
 * the whole shape of this screen: Stage 1 states system size, panel count and annual
 * production, and price arrives at Stage 2a gated on legal clearance. Two tests in
 * `Step3Result.test.tsx` enforce it mechanically, one for currency and one for benefit
 * language, because "no savings claim" is not something a reviewer reliably catches by
 * eye.
 *
 * `web/` COMPUTES NOTHING IT DISPLAYS, per master spec 4.1 rule 3. Every number here
 * comes off `EstimateResponse` as the server produced it; the only thing this file does
 * is round and punctuate. Deriving anything here would put a second implementation of
 * the model in the browser, where it could silently disagree with the lead already in
 * the CRM.
 */
export function Step3Result({ state, aerial }: Step3Props) {
  const { estimate, resolved } = state;

  useEffect(() => {
    if (!estimate) return;
    track('step_view', {
      step: 'result',
      submission_id: state.submissionId,
      status: estimate.status,
    });
  }, [estimate?.status]);

  // Not reachable through the funnel: step 2 sets `estimate` in the same patch that
  // sets `step: 'result'`. Guarded because the alternative is a thrown render inside a
  // cross-origin iframe, where the customer gets a white rectangle on the marketing
  // page and no way out.
  if (!estimate) return null;

  return (
    <section class="step">
      {estimate.status === 'ok' ? (
        <>
          <h2>Here is what your roof can fit</h2>

          <div class="verdict">
            <p>
              <strong>{estimate.panelsCount} panels</strong>, about{' '}
              <strong>{kw.format(estimate.arrayKwDc)} kW</strong>, producing an estimated{' '}
              <strong>{kwh.format(estimate.annualAcKwh)} kWh</strong> a year.
            </p>
            {resolved && <p class="place">{resolved.formatted}</p>}
          </div>

          {aerial}

          {/*
            An estimate, and said so. The figures come from Google's model of the roof
            plus our own derate chain, not from a site visit, and a designer revises
            them. Stating that here is what keeps the number from reading as a
            commitment, which matters more once Stage 2a attaches a price to it.
          */}
          <p class="provenance">
            Measured from aerial imagery of your roof. A Kasselman Solar designer will
            confirm the layout and get in touch.
          </p>
        </>
      ) : (
        <>
          <h2>We will take a closer look at this roof</h2>

          {/*
            NEVER AN ERROR, and the copy is load-bearing. This screen is reached because
            the submission SUCCEEDED: the lead has already fired, flagged, and a person
            is going to pick it up. Error language would be false, and it would also
            tell the customer to go away when what has actually happened is that they
            are in the queue. ADR-0016.

            The customer is told one thing here whether Google had no model for the roof
            or Google was down; only the lead records which (ADR-0026).
          */}
          <p class="verdict" role="status">
            Our aerial model does not cover this roof in enough detail to size it
            automatically. A Kasselman Solar designer will look at it by hand and get in
            touch with what it can fit.
          </p>

          {resolved && <p class="place">{resolved.formatted}</p>}
        </>
      )}
    </section>
  );
}
